// Session state: the design graph, grounding, facts, constraints, reviews.
// Nodes are born detached; only navigation commits them to the graph
// (registerNode), so speculative prefetch can't pollute the map/coverage.

export interface Option { label: string; tradeoff: string }
export type Stakes = "low" | "med" | "high";
export type VerdictKind = "sound" | "weak" | "risky";
export interface Verdict { verdict: VerdictKind; note: string; better: string }

export interface PrefetchSlot { status: "loading" | "ready" | "error"; node: DecisionNode | null }

export interface DecisionNode {
  id: string;
  parentId: string | null;
  depth: number;
  branch: string;
  question: string;
  options: Option[];
  chosenIndex: number | null;
  recommend: number;
  stakes: Stakes;
  confidence: number;
  prefetch: Record<number, PrefetchSlot>;
  childId: Record<number, string>;
  lastLatency: number | null;
  verdict?: Verdict;
}

export interface GroundingMetric { k: string; v: string }
export interface Constraint { text: string; branches: string[] }

export type ReviewEvent =
  | { t: "note"; nodeId: string; verdict?: VerdictKind; note?: string; suggest?: string }
  | { t: "branch"; fromNodeId: string; optionIndex: number; label: string; nodes: { question: string; take: string; why?: string }[] }
  | { t: "summary"; text: string };

export interface ReviewLayer {
  id: string;
  author: string;
  agent: string;
  reviewerSession: string;
  createdAt: number;
  status: string;
  seq: number;
  events: ReviewEvent[];
  enabled: boolean;
}

export interface DocState {
  secs: Record<string, string>;   // template section name -> accumulated text
  folded: string[];               // node ids already routed into the doc
}

export interface SessionState {
  idea: string;
  nodes: Map<string, DecisionNode>;
  rootId: string | null;
  currentId: string | null;
  facts: string[];
  grounding: GroundingMetric[];
  constraints: Constraint[];
  seq: number;
  sessionId: string;
  reviews: ReviewLayer[];
  doc: DocState;
}

export const newSessionId = () => `s${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;

export const state: SessionState = {
  idea: "",
  nodes: new Map(),
  rootId: null,
  currentId: null,
  facts: [],
  grounding: [],
  constraints: [],
  seq: 0,
  sessionId: `s${Date.now().toString(36)}boot`,   // replaced on reset/load
  reviews: [],
  doc: { secs: {}, folded: [] },
};

const nid = () => `n${++state.seq}`;

export function newNode(partial: Partial<DecisionNode>): DecisionNode {
  return {
    id: nid(), parentId: null, depth: 0, branch: "",
    question: "", options: [], chosenIndex: null,
    recommend: 0, stakes: "med",
    confidence: 0.15, prefetch: {}, childId: {},
    lastLatency: null,
    ...partial,
  };
}

export function registerNode(n: DecisionNode): DecisionNode {
  state.nodes.set(n.id, n);
  return n;
}

/** Breadcrumb of resolved decisions from root down to (and including) node. */
export function pathTo(node: DecisionNode): DecisionNode[] {
  const chain: DecisionNode[] = [];
  let cur: DecisionNode | null = node;
  while (cur) { chain.unshift(cur); cur = cur.parentId ? state.nodes.get(cur.parentId) ?? null : null; }
  return chain;
}

/**
 * The branch a node belongs to = the root option chosen to reach its
 * depth-1 ancestor. Group by that label (not the model's free-text branch
 * field, which drifts) so map/coverage grouping stays consistent.
 */
export function topBranchOf(node: DecisionNode): string {
  const chain = pathTo(node);
  if (chain.length < 2) return node.branch;
  const root = chain[0]!, d1 = chain[1]!;
  const entry = Object.entries(root.childId).find(([, cid]) => cid === d1.id);
  return entry ? root.options[+entry[0]!]?.label ?? d1.branch : (d1.branch || node.branch);
}

export function groundingLine(empty = "(grounding pending)"): string {
  if (!state.grounding.length) return empty;
  return state.grounding.map((g) => `${g.k}=${g.v}`).join(", ");
}

/** Start fresh under a new id — the previous session stays in the library. */
export function resetSessionState(): void {
  Object.assign(state, {
    idea: "", nodes: new Map(), rootId: null, currentId: null,
    facts: [], grounding: [], constraints: [], seq: 0, sessionId: newSessionId(), reviews: [],
    doc: { secs: {}, folded: [] },
  });
}
